import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { LoadingSpinner } from "../ActivityIndicators/LoadingSpinner";

const buttonVariants = cva(
  "cursor-pointer border inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-brand border-[#E0BB29] text-primary-foreground shadow hover:bg-primary",
        black:
          "bg-neutral-darkest border-neutral-darkest text-white shadow hover:bg-neutral-darkest/90",
        destructive:
          "bg-destructive border-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border-2 border-black bg-transparent hover:bg-accent hover:text-accent-foreground",
        outlineWhite:
          "border-2 border-white bg-transparent hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary border-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost:
          "border-transparent hover:bg-accent hover:text-accent-foreground",
        link: "border-0 text-primary underline-offset-4 hover:underline font-normal",
        flatWhite: "border-white bg-white text-black font-normal",
        flatDark: "border-black bg-black-light text-white font-normal",
        map: "border-0 bg-white text-[#060606] font-normal shadow-2xl [&_svg]:size-[24px]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-[44px] rounded-md px-5 font-medium text-base",
        icon: "h-10 w-11.5 [&_svg:not([class*=size-])]:size-6",
        map: "h-[36px] w-[40px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  loading,
  hideContentWhenLoading,
  type = "button",
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    hideContentWhenLoading?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  const content = (
    <>
      <LoadingSpinner
        className={hideContentWhenLoading ? "" : "-ml-1 mr-2"}
        isActive={!!loading}
      />
      {!hideContentWhenLoading || !loading ? children : null}
    </>
  );

  return asChild ? (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      type={type}
      {...props}
    >
      {children}
    </Comp>
  ) : (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={loading}
      type={type}
      {...props}
    >
      {content}
    </Comp>
  );
}

export { Button, buttonVariants };
